<template>
  <Transition name="slide-y-reverse-transition">
    <div
      v-show="show"
      v-shortkey="{ meta: ['meta', 'k'], ctrl: ['ctrl', 'k'] }"
      class="portal-component"
      @shortkey="handleHotkey"
      @keydown.up.stop="handleArrowUp"
      @keydown.down.stop="handleArrowDown"
      @keydown.enter.stop="handleEnter"
      @keydown.esc="show = false"
      @click="show = false"
    >
      <div class="dropdown-container">
        <Transition name="slide-y-reverse-transition">
          <div v-show="show" class="dropdown">
            <VTextField
              ref="textfield"
              v-model="input"
              placeholder="Jump to …"
              hideDetails
              class="portal-input mb-2"
              @click.stop
            />
            <div class="actions-container">
              <div class="actions" tabindex="0">
                <TransitionGroup name="list-complete" tag="div">
                  <div v-if="!filteredActions.length" key="$$$instructions" class="instructions">
                    <p>
                      Quickly jump to another page, app or bucket by searching for it.
                    </p>
                    <p>
                      Start with
                      <span class="code" @click.stop="prefill('doc')">
                        doc
                      </span>
                      or
                      <span class="code" @click.stop="prefill('?')">
                        ?
                      </span>
                      to search for documentation.
                    </p>
                  </div>
                  <template v-else>
                    <VCard
                      v-for="(action, index) in filteredActions"
                      :key="action.getKey()"
                      :ref="'action' + index"
                      class="action px-3 py-2 contrast mb-2 list-complete-item"
                      tabindex="-1"
                      :class="{ focused: focusIndex === index }"
                      @click.native.stop="action.trigger()"
                    >
                      <KmIcon
                        v-if="action.getIcon() !== undefined"
                        class="category-icon pr-2"
                        :name="action.getIcon()"
                      />
                      <h1 class="font-light subheading">
                        {{ action.getName() }}
                      </h1>
                      <VSpacer />
                      <span>{{ action.getCategory() }}</span>
                    </VCard>
                  </template>
                </TransitionGroup>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </Transition>
</template>

<script>
import { isEqual } from 'lodash'
import { SET_IS_PORTAL_OPEN } from '../../store/mutations'
import getAppInfos from '@/utils/getAppInfos'
import { BUCKET_META_PAGE } from '@/router'
import createFilter from '@/utils/createFilter'
import { getLinkToApp, getLinkToBucket, isRouteUserFacing, hasFeature } from '@/utils/routeHelpers'
import alphabeticalSort from '@/utils/sort'
import KmIcon from '@/components/KmIcon'
import algoliaSearch from 'algoliasearch/lite'

class PortalAction {
  getKey() {
    throw new Error('unimplemented')
  }

  getName() {
    throw new Error('unimplemented')
  }

  getCategory() {
    throw new Error('unimplemented')
  }

  getIcon() {
    throw new Error('unimplemented')
  }

  trigger() {
    throw new Error('unimplemented')
  }
}

class BucketSwitchAction extends PortalAction {
  constructor(vm, bucket) {
    super()
    this._vm = vm
    this._bucket = bucket
  }

  getKey() {
    return this._bucket.id
  }

  getName() {
    return this._bucket.name
  }

  getCategory() {
    return 'Bucket'
  }

  getIcon() {
    return 'km-icon-bucket'
  }

  trigger() {
    this._vm.$router.push(getLinkToBucket(this._bucket.id))
  }
}

class AppSwitchAction extends PortalAction {
  constructor(vm, appName) {
    super()
    this._vm = vm
    this._appName = appName
  }

  getKey() {
    return this._appName
  }

  getName() {
    return this._appName
  }

  getCategory() {
    return 'App'
  }

  getIcon() {
    return getAppInfos(this._appName).icon
  }

  trigger() {
    this._vm.$router(getLinkToApp(this._appName))
  }
}

class PageSwitchAction extends PortalAction {
  constructor(vm, route, params) {
    super()
    this._vm = vm
    this._route = route
    this._params = params
  }

  getKey() {
    return this._route.name
  }

  getName() {
    return this._route.meta.title
  }

  getCategory() {
    return 'Page'
  }

  getIcon() {
    return this._route.meta.icon
  }

  trigger() {
    const store = this._vm.$store
    const params = {}
    const query = {}

    if (this._params.includes('id')) {
      params.id = store.getters.currentBucket.bucket.id
    }
    if (this._params.includes('appType')) {
      params.appType = store.getters.currentAppType
    }
    if (this._route.meta.requireApp) {
      query.app = store.getters.currentAppName
    }

    this._vm.$router.push({
      name: this._route.name,
      params,
      query,
    })
  }
}

class JumpToRoute {
  constructor(vm) {
    this._actions = this._createActions(vm)
  }

  _createActions(vm) {
    const actions = []

    Object.values(vm.$store.state.buckets).forEach(bucketState => {
      actions.push(new BucketSwitchAction(vm, bucketState.bucket))
    })

    const currentBucket = vm.$store.getters.currentBucket
    const currentAppName = vm.$store.getters.currentAppName

    if (currentBucket) {
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      vm.$store.getters.select({ appName: false }).appNames.forEach(appName => {
        actions.push(new AppSwitchAction(vm, appName))
      })
    }

    const collectRouteActions = (routes, params) => {
      routes.forEach(route => {
        if (!isRouteUserFacing(route) || !hasFeature(route)) return
        if (route.name === BUCKET_META_PAGE && !currentBucket) return
        if (route.meta.requireApp && !currentAppName) return

        const routeParams = params.slice()
        const regex = /:([a-zA-Z]+)/g
        let match = regex.exec(route.path)
        while (match !== null) {
          routeParams.push(match[1])
          match = regex.exec(route.path)
        }

        if (routeParams.every(param => ['appType', 'id'].includes(param))) {
          if (route.children) {
            collectRouteActions(route.children, routeParams)
          } else {
            actions.push(new PageSwitchAction(vm, route, routeParams))
          }
        }
      })
    }

    collectRouteActions(vm.$router.options.routes, [])

    return actions.sort((a, b) => alphabeticalSort(a._name, b._name))
  }

  search(query) {
    const filter = createFilter(query)
    return this._actions
      .filter(action => filter.matches(action.getName()) || filter.matches(action.getCategory()))
      .slice(0, 5)
  }
}

class DocHitAction extends PortalAction {
  constructor(hit) {
    super()
    this._hit = hit
  }

  getKey() {
    return this._hit.objectID
  }

  getName() {
    const hit = this._hit
    return (
      Object.keys(hit.hierarchy)
        .sort()
        .map(key => {
          if (key === 'lvl0') return // remove 'lvl0' titles
          return hit.hierarchy[key]
        })
        .filter(Boolean)
        .join(' > ') || 'Overview'
    )
  }

  getCategory() {
    const mainTitle = this._hit.hierarchy.lvl0
    return mainTitle === 'PM2 Plus'
      ? 'Plus'
      : mainTitle === 'PM2 Runtime'
      ? 'Runtime'
      : mainTitle === 'PM2 Enterprise'
      ? 'Enterprise'
      : ''
  }

  getIcon() {
    return 'km-icon-support'
  }

  trigger() {
    window.open(this._hit.url, '_blank')
  }
}

class JumpToDoc {
  constructor() {
    this._index = algoliaSearch('BH4D9OD16A', '65aa0e3b2df3e2c47669de31f26cc562').initIndex(
      'pm2docs',
    )
  }

  search(query) {
    return new Promise((resolve, reject) => {
      this._index.search(
        {
          query,
          hitsPerPage: 5,
        },
        (err, content) => {
          if (err) return reject(err)

          resolve(content.hits.map(hit => new DocHitAction(hit)))
        },
      )
    })
  }
}

const providers = new Map([['default', JumpToRoute], ['?', JumpToDoc], ['doc', JumpToDoc]])

export default {
  name: 'NavigationPortal',
  components: {
    KmIcon,
  },

  data() {
    return {
      input: '',
      focusIndex: 0,
      filteredActions: [],
    }
  },

  computed: {
    show: {
      get() {
        return this.$store.state.portal.isOpen
      },
      set(value) {
        this.$store.commit(SET_IS_PORTAL_OPEN, { isOpen: value })
      },
    },

    prefixAndQuery() {
      const prefix = Array.from(providers.keys()).find(prefix => this.input.startsWith(prefix))
      if (prefix) {
        return [prefix, this.input.slice(prefix.length).trim()]
      }
      return ['default', this.input]
    },

    query() {
      return this.prefixAndQuery[1]
    },
  },

  watch: {
    show(newVal, oldVal) {
      if (newVal) {
        // Refresh possible actions
        this.provider = null
        this.input = this.$store.state.portal.initialInput
        this.$nextTick(() => {
          this.$refs.textfield.focus()
        })
      } else {
        this.input = ''
      }
    },

    prefixAndQuery: {
      handler([prefix, query], previous) {
        if (!this.provider || !previous || prefix !== previous[0]) {
          // The prefix did change, instanciate the corresponding provider
          this.provider = new (providers.get(prefix))(this) // eslint-disable-line new-cap
        }

        const searchToken = (this.searchToken = {})
        if (!query) {
          this.filteredActions = []
        } else {
          Promise.resolve(this.provider.search(query)).then(results => {
            // Verify that the search has not been cancelled
            if (this.searchToken !== searchToken) return

            this.filteredActions = results
          })
        }
      },
      immediate: true,
    },

    filteredActions(newVal, oldVal) {
      if (newVal.length > 0 && !isEqual(newVal, oldVal)) {
        this.focusIndex = 0
      }
    },
  },

  methods: {
    handleHotkey() {
      this.show = !this.show
    },

    handleArrowUp() {
      this.focusIndex--
      if (this.focusIndex < 0) this.focusIndex = this.filteredActions.length - 1
    },

    handleArrowDown() {
      this.focusIndex++
      if (this.focusIndex > this.filteredActions.length - 1) this.focusIndex = 0
    },

    handleEnter() {
      if (this.filteredActions.length === 0) return
      this.triggerAction(this.focusIndex)
      this.show = false
    },

    triggerAction(index) {
      this.filteredActions[index].trigger()
    },

    prefill(text) {
      this.input = text + ' ' + this.query
      this.$nextTick(() => {
        this.$refs.textfield.focus()
      })
    },
  },
}
</script>

<style lang="postcss" scoped>
.portal-component {
  position: fixed;
  z-index: 90;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  transition-duration: 0.2s;

  &:before {
    content: '';
    background: var(--base);
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.9;
  }

  & .dropdown-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 30vh;

    & .dropdown {
      position: relative;
      max-width: 600px;
      width: 600px;
      flex: 1;
      transition-delay: 100ms;
    }

    & .actions-container {
      position: absolute;
      top: 74;
      left: 0;
      width: 100%;

      & .action {
        display: flex;
        flex-direction: row;
        align-items: center;
        cursor: pointer;

        & .category-icon {
          color: inherit;
        }

        &.focused {
          color: var(--primary);
          transform: scale(1.05);
          outline: none;
        }
      }
    }
  }

  & >>> input {
    font-size: var(--fs-display-1) !important;
    min-height: 64px !important;
  }
}

.list-complete-item {
  transition: all 0.1s ease-in-out;
}
.list-complete-enter, .list-complete-leave-to
/* .list-complete-leave-active below version 2.1.8 */ {
  opacity: 0;
  transform: scale(0.7);
}
.list-complete-leave-active {
  position: absolute;
  width: 100%;
}

.code {
  display: inline-block;
  border: 1px solid var(--text);
  border-radius: 4px;
  padding: 0 8px;
  margin: 0 4px;
  font-family: var(--font-monospace-regular);
  cursor: pointer;
}
</style>
